import { ServiceBusClient, delay } from "@azure/service-bus";

const DEFAULT_EXCEPTION_TOPIC = 'exception-handler-for-ciep';

const getParsedStorageValue = (key) => {
	try {
		const value = localStorage.getItem(key);
		if (!value) return null;
		return JSON.parse(value);
	} catch {
		return null;
	}
};

const getDecodedBase64Json = (key) => {
	try {
		const value = localStorage.getItem(key);
		if (!value) return null;
		return JSON.parse(window.atob(value));
	} catch {
		return null;
	}
};

const buildPayload = ({
	error,
	context = {},
	source = 'unknown',
	eventType = 'UNHANDLED_EXCEPTION',
	topic = DEFAULT_EXCEPTION_TOPIC,
	customPayload = {},
}) => {
	const jwtUserData = getParsedStorageValue('jwtuserData');
	const adConfig = getDecodedBase64Json('adConfig');
	const userEmailReport = getDecodedBase64Json('userEmailReport');
	const resolvedUserEmail =
		jwtUserData?.email ||
		jwtUserData?.user?.email ||
		userEmailReport?.email ||
		adConfig?.loginHint ||
		localStorage.getItem('userEmail') ||
		'';

	return {
		topic,
		source,
		eventType,
		timestamp: new Date().toISOString(),
		path: window.location.pathname,
		user: {
			email: resolvedUserEmail,
			userId: localStorage.getItem('userId') || '',
		},
		localStorageContext: {
			isAdLogin: localStorage.getItem('isAdLogin') || '',
			isSsoLogin: localStorage.getItem('isSsoLogin') || '',
			hasAdConfig: !!localStorage.getItem('adConfig'),
			hasSsoConfig: !!localStorage.getItem('ssoConfig'),
			hasUserEmailReport: !!localStorage.getItem('userEmailReport'),
			hasJwtUserData: !!localStorage.getItem('jwtuserData'),
			hasJwtToken: !!localStorage.getItem('jwt_access_token'),
			hasRefreshToken: !!localStorage.getItem('refreshToken'),
			llse: localStorage.getItem('llse') || '',
			ssoRedirect: localStorage.getItem('ssoRedirect') || '',
			isSsoRedirectSuccess: localStorage.getItem('isSsoRedirectSuccess') || '',
		},
		error: {
			message: error?.message || 'Unknown error',
			name: error?.name || 'Error',
			stack: error?.stack || '',
		},
		context,
		...customPayload,
	};
};

export const publishExceptionMessage = async ({
	error,
	context = {},
	source = 'unknown',
	eventType = 'UNHANDLED_EXCEPTION',
	topic = DEFAULT_EXCEPTION_TOPIC,
	customPayload = {},
}) => {
	const connectionString = process.env.REACT_APP_SERVICE_BUS_CONNECTION_STRING;
	if (!connectionString) {
		console.warn('[ExceptionPublisher] REACT_APP_SERVICE_BUS_CONNECTION_STRING is not configured. Skipping exception publish.');
		return;
	}

	const payload = buildPayload({ error, context, source, eventType, topic, customPayload });
	let sbClient = null;
	let sender = null;

	const sendMessage = async () => {
		sbClient = new ServiceBusClient(connectionString);
		sender = sbClient.createSender(topic);
		await sender.sendMessages({
			body: payload,
			contentType: 'application/json',
			subject: eventType,
		});
	};

	try {
		await sendMessage();
		console.log('[ExceptionPublisher] Exception event published to topic:', topic);
	} catch (publishError) {
		console.error('[ExceptionPublisher] Failed to publish exception event. Retrying once...', publishError);
		try {
			await delay(500);
			if (sender) {
				await sender.close();
				sender = null;
			}
			if (sbClient) {
				await sbClient.close();
				sbClient = null;
			}
			await sendMessage();
			console.log('[ExceptionPublisher] Exception event published on retry to topic:', topic);
		} catch (retryError) {
			console.error('[ExceptionPublisher] Retry failed to publish exception event:', retryError);
		}
	} finally {
		if (sender) {
			await sender.close();
		}
		if (sbClient) {
			await sbClient.close();
		}
	}
};

// Backward-compatible helper for existing MFA usage.
export const publishMfaException = async (error, context = {}) => {
	return publishExceptionMessage({
		error,
		context,
		source: 'MFA/index.js',
		eventType: 'MFA_REDIRECT_FAILURE',
		topic: DEFAULT_EXCEPTION_TOPIC,
	});
};
